import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import type { IdentifiedRoi } from '~/domain/comparison/roiCounterpart';
import { useQaModelRois } from '~/hooks/workflows/backend/tasks/useQaModelRois';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { roiPlanEntries, RoiPlanEntry, SourceRoi } from './roiPropagationPlan';
import { useReplanningStructureSets } from './useReplanningStructureSets';

export interface RoiPlanView {
    readonly sourceRois: readonly SourceRoi[];
    readonly modelRois: readonly IdentifiedRoi[];
    readonly entries: readonly RoiPlanEntry[];
}

export function useRoiPlan(): RoiPlanView {
    // The run's fixed source set: once propagation starts the propagated set is current.
    const { source } = useReplanningStructureSets();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const sourceSet = source ?? currentStructureSet;
    const task = useRecoilValue(contourPropagationTaskState);
    const modelRois = useQaModelRois(task.comparisonModel);

    const sourceRois = useMemo(
        () =>
            sourceSet?.roiArray?.map((roi) => ({
                name: roi.name,
                interpretedType: roi.interpretedType,
                code: roi.identificationCode?.codeValue
                    ? {
                          value: roi.identificationCode.codeValue,
                          scheme: roi.identificationCode.codingSchemeDesignator,
                          meaning: roi.identificationCode.codeMeaning,
                      }
                    : undefined,
            })) ?? [],
        [sourceSet]
    );
    const entries = useMemo(
        () => roiPlanEntries(sourceRois, modelRois, task.roiPlan ?? {}),
        [sourceRois, modelRois, task.roiPlan]
    );

    return { sourceRois, modelRois, entries };
}
